import React from 'react';
import { ChangeOrderWithDetails } from '../../services/change-order.service';

interface ChangeOrderListProps {
  orders: ChangeOrderWithDetails[];
  onSelect: (order: ChangeOrderWithDetails) => void;
  onApprove?: (id: string) => Promise<void>;
  onReject?: (id: string, reason: string) => Promise<void>;
  onExecute?: (id: string) => Promise<void>;
}

export function ChangeOrderList({ orders, onSelect, onApprove, onReject, onExecute }: ChangeOrderListProps) {
  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      executed: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      pending: '⏳ Pendiente',
      approved: '✅ Aprobado',
      rejected: '❌ Rechazado',
      executed: '📋 Ejecutado'
    };
    return labels[status] || status;
  };

  const getReasonLabel = (reason: string): string => {
    const labels: Record<string, string> = {
      design_change: 'Cambio de diseño',
      material_change: 'Cambio de materiales',
      scope_change: 'Cambio de alcance',
      unforeseen: 'Imprevisto'
    };
    return labels[reason] || reason;
  };

  if (orders.length === 0) {
    return (
      <div className="text-center py-8 bg-white rounded-lg shadow">
        <p className="text-gray-500">No hay órdenes de cambio registradas</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orders.map((order) => (
        <div
          key={order.id}
          className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onSelect(order)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-semibold text-gray-800">{order.description}</h4>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(order.status)}`}>
                  {getStatusLabel(order.status)}
                </span>
              </div>
              <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                <span>📋 {getReasonLabel(order.reason)}</span>
                <span>📦 {order.items?.length || 0} items</span>
                <span>💰 ${order.costImpact?.toFixed(2) || '0.00'}</span>
                {order.status === 'approved' && (
                  <span className="text-green-600">✓ Aprobado por: {order.approvedByName || 'N/A'}</span>
                )}
                <span className="text-gray-400 text-xs">
                  {new Date(order.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="flex gap-2 ml-4">
              {order.status === 'pending' && onApprove && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove(order.id);
                  }}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                >
                  Aprobar
                </button>
              )}
              {order.status === 'approved' && onExecute && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onExecute(order.id);
                  }}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                >
                  Ejecutar
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}